import {
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    default as UploadWidgetProps,
    UploadQueueItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Text
} from "ncore-ui-kit";
import {
    AlertCircleIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClockIcon,
    XIcon
} from "lucide-react-native";

const UploadWidget = ({
    onToggleMinimize,
    isMinimized,
    folderName,
    onClose,
    isOpen,
    items
}: UploadWidgetProps) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    if(!isOpen || items.length === 0) {
        return null;
    }

    const totalCount = items.length;
    const completedCount = items.filter((item) => item.status === "completed").length;
    const errorCount = items.filter((item) => item.status === "error").length;
    const overallPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
    const isAllDone = (completedCount + errorCount) === totalCount;

    const renderStatusIcon = (status: UploadQueueItem["status"]) => {
        if(status === "completed") {
            return <CheckCircleIcon
                color={colors.system.default.success}
                size={18}
            />;
        }

        if(status === "error") {
            return <AlertCircleIcon
                color={colors.system.default.error}
                size={18}
            />;
        }

        if(status === "uploading") {
            return <ActivityIndicator
                color={colors.content.icon.high}
                size="small"
            />;
        }

        return <ClockIcon
            color={colors.content.icon.disabled}
            size={18}
        />;
    };

    const renderItem = (item: UploadQueueItem) => {
        return <View
            style={[
                stylesheet.itemContainer,
                {
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingSm
                }
            ]}
            key={item.id}
        >
            <View
                style={[
                    stylesheet.itemInfoContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={stylesheet.itemTitleContainer}
                >
                    <Text
                        variant="bodySmallSize"
                        numberOfLines={1}
                    >
                        {item.fileName}
                    </Text>
                    {
                        item.folderPath ?
                            <Text
                                customColor={colors.content.text.disabled}
                                variant="labelSmallSize"
                                numberOfLines={1}
                            >
                                {item.folderPath}
                            </Text>
                        :
                            null
                    }
                </View>
            </View>
            <View
                style={stylesheet.itemStatusContainer}
            >
                {renderStatusIcon(item.status)}
            </View>
        </View>;
    };

    return <View
        style={[
            stylesheet.container,
            {
                backgroundColor: colors.project.brandWhite,
                borderColor: colors.project.brandBorder,
                borderWidth: borders.seperator,
                borderRadius: radiuses.md
            }
        ]}
    >
        <View
            style={[
                stylesheet.headerContainer,
                {
                    backgroundColor: colors.content.container.subtle,
                    paddingHorizontal: spaces.spacingMd,
                    paddingVertical: spaces.spacingSm,
                    gap: spaces.spacingSm
                }
            ]}
        >
            <View
                style={[
                    stylesheet.titleContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Text
                    variant="bodyMediumSize"
                    style={{
                        fontWeight: "600"
                    }}
                    numberOfLines={1}
                >
                    {isAllDone ? (localize("drive-upload-completed") || "Yükleme Tamamlandı") : `${localize("drive-uploading") || "Yükleniyor"}: ${folderName}`}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.actionsContainer,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <TouchableOpacity
                    onPress={onToggleMinimize}
                    activeOpacity={0.7}
                >
                    {
                        isMinimized ?
                            <ChevronUpIcon
                                color={colors.content.icon.mid}
                                size={20}
                            />
                        :
                            <ChevronDownIcon
                                color={colors.content.icon.mid}
                                size={20}
                            />
                    }
                </TouchableOpacity>
                <TouchableOpacity
                    activeOpacity={0.7}
                    onPress={onClose}
                >
                    <XIcon
                        color={colors.content.icon.mid}
                        size={20}
                    />
                </TouchableOpacity>
            </View>
        </View>
        <View
            style={[
                stylesheet.progressBarContainer,
                {
                    backgroundColor: colors.content.container.subtle
                }
            ]}
        >
            <View
                style={[
                    stylesheet.progressBarFill,
                    {
                        backgroundColor: isAllDone ? colors.system.default.success : colors.system.default.info,
                        width: `${overallPercent}%`
                    }
                ]}
            />
        </View>
        {
            !isMinimized ?
                <View
                    style={{
                        paddingVertical: spaces.spacingXs
                    }}
                >
                    <View
                        style={{
                            paddingHorizontal: spaces.spacingMd,
                            paddingVertical: spaces.spacingXs
                        }}
                    >
                        <Text
                            customColor={colors.content.text.disabled}
                            variant="labelSmallSize"
                        >
                            {`${completedCount}/${totalCount} ${localize("drive-files-uploaded") || "dosya yüklendi"} (%${overallPercent})`}
                        </Text>
                    </View>
                    <ScrollView
                        style={stylesheet.contentContainer}
                    >
                        {items.map(renderItem)}
                    </ScrollView>
                </View>
            :
                null
        }
    </View>;
};
export default UploadWidget;
